import { z } from 'zod'
import { translations, type Lang } from '@/lib/i18n'

const BUDGET_OPTIONS_EN = translations.en.contact.budgetOpts
const BUDGET_OPTIONS_FA = translations.fa.contact.budgetOpts
const ALL_BUDGET_OPTIONS = new Set<string>([...BUDGET_OPTIONS_EN, ...BUDGET_OPTIONS_FA])

/**
 * Build a Zod schema for the contact form. Parameterizing by language lets
 * client-side RHF show Farsi or English error messages without an extra
 * translation step; the server-side route handler defaults to English.
 *
 * `budget` is optional but, when provided, must match one of the listed
 * options in either language so people pasting the FA strings into an EN
 * session (or vice versa) still pass.
 */
export function buildContactSchema(lang: Lang = 'en') {
  const e = translations[lang].contact.errors

  return z.object({
    name: z
      .string({ message: e.nameRequired })
      .trim()
      .min(1, e.nameRequired)
      .max(100, e.nameTooLong),
    email: z
      .string({ message: e.emailRequired })
      .trim()
      .min(1, e.emailRequired)
      .email(e.emailInvalid)
      .max(254, e.emailInvalid),
    company: z
      .string({ message: e.companyRequired })
      .trim()
      .min(1, e.companyRequired)
      .max(200, e.companyTooLong),
    budget: z
      .string()
      .optional()
      .refine((v) => !v || ALL_BUDGET_OPTIONS.has(v), { message: e.budgetInvalid }),
    message: z.string().trim().max(5000, e.messageTooLong).optional(),
  })
}

/** Server-side schema used by the API route — English error messages only. */
export const contactServerSchema = buildContactSchema('en')

export type ContactFormData = z.infer<ReturnType<typeof buildContactSchema>>
